import {
  Tab,
  TabEntityRef,
  TabTarget,
  tabKey,
  targetsEqual,
  targetsSameEntity,
} from "./tabTarget";

/**
 * Every mutation of the tab strip, as pure functions over `TabsState`.
 *
 * Navigation deliberately does not happen here. The route is the source of truth for which tab is
 * active, so callers navigate and let `useSyncTabsWithRoute` fold the result back into this state —
 * doing both from one place is what made the previous implementation mutually recursive.
 */
export type TabsState = {
  tabs: Tab[];
  activeTabId: string | null;
};

export const emptyTabsState: TabsState = { tabs: [], activeTabId: null };

export function findTabByTarget(
  tabs: Tab[],
  target: TabTarget,
): Tab | undefined {
  const key = tabKey(target);
  if (key == null) return undefined;
  return tabs.find((tab) => tabKey(tab.target) === key);
}

export function findTabById(tabs: Tab[], tabId: string | null) {
  if (tabId == null) return undefined;
  return tabs.find((tab) => tab.id === tabId);
}

export function getActiveTab(state: TabsState) {
  return findTabById(state.tabs, state.activeTabId);
}

type OpenTabOptions = {
  /** Id for the tab if a new one has to be created. */
  newTabId: string;
  cachedLabel?: string;
  /** Defaults to true. */
  activate?: boolean;
};

/**
 * Opens `target`, reusing an existing tab when one already points at it.
 *
 * Reuse re-points the tab at the new target so a model opened from a dashboard tile picks up that
 * tile's context rather than keeping whichever context it was opened with first.
 *
 * An active scratch tab is consumed rather than left behind — clicking a model from a fresh empty
 * tab should fill that tab in, not open a second one.
 */
export function openTab(
  state: TabsState,
  target: TabTarget,
  options: OpenTabOptions,
): TabsState {
  const activate = options.activate ?? true;
  const existing = findTabByTarget(state.tabs, target);

  if (existing) {
    const cachedLabel = options.cachedLabel ?? existing.cachedLabel;
    // Identity-stable when nothing actually changed: the route sync calls this on every navigation,
    // and a fresh state object each time would rewrite storage and re-render the strip for nothing.
    const isUnchanged =
      targetsEqual(existing.target, target) &&
      cachedLabel === existing.cachedLabel;
    const withTarget: TabsState = isUnchanged
      ? state
      : {
          tabs: state.tabs.map((tab) =>
            tab.id === existing.id ? { ...existing, target, cachedLabel } : tab,
          ),
          activeTabId: state.activeTabId,
        };
    return activate ? activateTab(withTarget, existing.id) : withTarget;
  }

  const activeTab = getActiveTab(state);
  if (activeTab?.target.kind === "scratch" && target.kind !== "scratch") {
    const updated: Tab = {
      ...activeTab,
      target,
      cachedLabel: options.cachedLabel,
    };
    return {
      tabs: state.tabs.map((tab) => (tab.id === activeTab.id ? updated : tab)),
      activeTabId: activate ? activeTab.id : state.activeTabId,
    };
  }

  const newTab: Tab = {
    id: options.newTabId,
    target,
    cachedLabel: options.cachedLabel,
  };
  return {
    tabs: [...state.tabs, newTab],
    activeTabId: activate ? newTab.id : state.activeTabId,
  };
}

const SCRATCH_LABEL = "New tab";

/**
 * Empty tabs are told apart by a number fixed when they are created, the way unsaved drafts are, and
 * the lowest free number is reused. Numbering by position instead would relabel the tabs either side
 * of one the user closes.
 */
export function nextScratchLabel(tabs: Tab[]): string {
  const taken = new Set(
    tabs
      .filter((tab) => tab.target.kind === "scratch")
      .map((tab) => tab.cachedLabel),
  );
  for (let n = 1; ; n++) {
    const label = n === 1 ? SCRATCH_LABEL : `${SCRATCH_LABEL} ${n}`;
    if (!taken.has(label)) return label;
  }
}

/**
 * Folds the target the URL points at into the strip.
 *
 * Scratch is the awkward case: `/editor` cannot tell one empty tab from another, so the route never
 * creates or replaces one. It activates an existing scratch tab if there is one and otherwise clears
 * the selection — which is what that route actually renders, and what closing the last tab must
 * leave behind.
 */
export function syncRouteTarget(
  state: TabsState,
  target: TabTarget,
  newTabId: string,
): TabsState {
  if (target.kind === "scratch") {
    if (getActiveTab(state)?.target.kind === "scratch") return state;
    const scratch = state.tabs.find((tab) => tab.target.kind === "scratch");
    return activateTab(state, scratch?.id ?? null);
  }
  return openTab(state, target, { newTabId });
}

export function activateTab(state: TabsState, tabId: string | null): TabsState {
  if (tabId != null && findTabById(state.tabs, tabId) == null) return state;
  if (state.activeTabId === tabId) return state;
  return { ...state, activeTabId: tabId };
}

export function updateTab(
  state: TabsState,
  tabId: string,
  patch: Partial<Omit<Tab, "id">>,
): TabsState {
  const tab = findTabById(state.tabs, tabId);
  if (!tab) return state;
  const keys = Object.keys(patch) as (keyof typeof patch)[];
  if (keys.every((key) => tab[key] === patch[key])) return state;
  return {
    ...state,
    tabs: state.tabs.map((t) => (t.id === tabId ? { ...t, ...patch } : t)),
  };
}

/**
 * Removes every tab except those `shouldKeep` accepts, and picks a new active tab when the active
 * one goes. This is the single place the "which tab becomes active" rule lives; the strip's close
 * button and the keyboard shortcut used to disagree about it.
 */
export function keepTabs(
  state: TabsState,
  shouldKeep: (tab: Tab) => boolean,
): TabsState {
  const kept = state.tabs.filter(shouldKeep);
  if (kept.length === state.tabs.length) return state;

  if (
    state.activeTabId != null &&
    kept.some((t) => t.id === state.activeTabId)
  ) {
    return { tabs: kept, activeTabId: state.activeTabId };
  }

  return { tabs: kept, activeTabId: nextActiveTabId(state, kept) };
}

/**
 * The tab to the left of where the active one was, falling back to the first remaining tab. Chosen
 * to match the long-standing keyboard-shortcut behaviour.
 */
function nextActiveTabId(state: TabsState, kept: Tab[]): string | null {
  if (kept.length === 0) return null;
  const removedIndex = state.tabs.findIndex((t) => t.id === state.activeTabId);
  if (removedIndex === -1) return kept[0].id;
  const keptBefore = state.tabs
    .slice(0, removedIndex)
    .filter((t) => kept.some((k) => k.id === t.id));
  return keptBefore.length > 0
    ? keptBefore[keptBefore.length - 1].id
    : kept[0].id;
}

export function closeTab(state: TabsState, tabId: string): TabsState {
  return keepTabs(state, (tab) => tab.id !== tabId);
}

export function closeTabs(state: TabsState, tabIds: string[]): TabsState {
  const closing = new Set(tabIds);
  return keepTabs(state, (tab) => !closing.has(tab.id));
}

export function closeOtherTabs(state: TabsState, tabId: string): TabsState {
  if (findTabById(state.tabs, tabId) == null) return state;
  return {
    tabs: state.tabs.filter((tab) => tab.id === tabId),
    activeTabId: tabId,
  };
}

export function closeTabsToTheRight(
  state: TabsState,
  tabId: string,
): TabsState {
  const index = state.tabs.findIndex((tab) => tab.id === tabId);
  if (index === -1) return state;
  const keptIds = new Set(state.tabs.slice(0, index + 1).map((tab) => tab.id));
  return keepTabs(state, (tab) => keptIds.has(tab.id));
}

export function closeAllTabs(): TabsState {
  return emptyTabsState;
}

/** Drops every tab pointing at `ref`, for entities the user just deleted. */
export function closeTabsForEntity(
  state: TabsState,
  ref: TabEntityRef,
): TabsState {
  return keepTabs(state, (tab) => !targetsSameEntity(tab.target, ref));
}

/**
 * Re-points the tabs open on `ref` at `target`, for an entity that has turned into another one — an
 * unsaved draft saved as a model. The tab keeps its place, its id and the selection. If `target`
 * already has a tab, that tab takes over instead and the replaced ones are dropped.
 */
export function replaceEntity(
  state: TabsState,
  ref: TabEntityRef,
  target: TabTarget,
): TabsState {
  const replaced = state.tabs.filter((tab) =>
    targetsSameEntity(tab.target, ref),
  );
  if (replaced.length === 0) return state;

  const replacedIds = new Set(replaced.map((tab) => tab.id));
  const existing = findTabByTarget(state.tabs, target);
  const survivorId = existing?.id ?? replaced[0].id;

  const tabs = state.tabs
    .filter((tab) => tab.id === survivorId || !replacedIds.has(tab.id))
    .map((tab) =>
      tab.id === survivorId && !existing
        ? { id: tab.id, target, cachedLabel: undefined }
        : tab,
    );
  const activeTabId =
    state.activeTabId != null && replacedIds.has(state.activeTabId)
      ? survivorId
      : state.activeTabId;
  return { tabs, activeTabId };
}

/**
 * Reorders to match `orderedIds`, ignoring ids it does not know. Tabs missing from the list keep
 * their relative order at the end, so a reorder racing with a close cannot drop a tab.
 */
export function reorderTabs(state: TabsState, orderedIds: string[]): TabsState {
  const byId = new Map(state.tabs.map((tab) => [tab.id, tab]));
  const ordered: Tab[] = [];
  for (const id of orderedIds) {
    const tab = byId.get(id);
    if (tab) {
      ordered.push(tab);
      byId.delete(id);
    }
  }
  if (ordered.length === 0) return state;
  return { ...state, tabs: [...ordered, ...byId.values()] };
}
